// src/components/QuizModal.js
import { useMemo, useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { isCorrectAnswer } from '../utils/answer';
import audioManager from '../utils/AudioManager';
import { saveAudioPlay } from '../utils/firebase';
import '../styles/QuizModal.css';

export default function QuizModal({ isOpen, onClose, quiz, onShowResult, sessionId }) {
  const { t, i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || i18n.language || 'ja').split('-')[0];

  const [selectedAnswers, setSelectedAnswers] = useState([]);
  // ★ 1回目:再生 → 2回目:停止 → 3回目:最初から再生、のトグル用state
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const isCompleted = !!quiz?.completed;

  const BADGE_SRC = isCompleted
    ? '/images/stamp/quizget-icon.svg'
    : '/images/stamp/quiz-icon.svg';

  const thumbnailSrc = quiz?.thumbnail || quiz?.image || '';

  // 多言語文字列ヘルパー（言語が変わったときだけ作り直す）
  const pickLocalizedText = useCallback(
    (val) => {
      if (!val) return '';
      if (typeof val === 'string') return val;
      if (typeof val === 'object') {
        return val[lang] || val.ja || Object.values(val)[0] || '';
      }
      return '';
    },
    [lang]
  );

  // --- 選択肢（quiz.data.choices / quiz.answers 両対応） ---
  const options = useMemo(() => {
    if (!quiz) return [];

    const dataChoices = quiz.data?.choices;
    const answersChoices = quiz.answers;

    // 多言語オブジェクト { ja: [...], en: [...], ... }
    const objectChoices =
      (dataChoices && typeof dataChoices === 'object' && !Array.isArray(dataChoices))
        ? dataChoices
        : (answersChoices && typeof answersChoices === 'object' && !Array.isArray(answersChoices))
          ? answersChoices
          : null;

    if (objectChoices) {
      const arr =
        objectChoices[lang] ||
        objectChoices.ja ||
        Object.values(objectChoices)[0];

      if (Array.isArray(arr)) return arr;
    }

    // 旧形式: 配列のまま
    if (Array.isArray(dataChoices)) return dataChoices;
    if (Array.isArray(answersChoices)) return answersChoices;
    if (Array.isArray(quiz.data?.answer)) return quiz.data.answer;

    return [];
  }, [quiz, lang]);

  // --- 正解テキスト（quiz.data.answer / quiz.answer 両対応） ---
  const correctText = useMemo(() => {
    if (!quiz) return '';

    const dataAnswer = quiz.data?.answer;
    const flatAnswer = quiz.answer;

    // 多言語オブジェクト優先
    if (dataAnswer && typeof dataAnswer === 'object') {
      return pickLocalizedText(dataAnswer);
    }
    if (flatAnswer && typeof flatAnswer === 'object') {
      return pickLocalizedText(flatAnswer);
    }

    // 旧形式: 文字列
    if (typeof flatAnswer === 'string') return flatAnswer;
    if (typeof dataAnswer === 'string') return dataAnswer;

    return '';
  }, [quiz, pickLocalizedText]);

  // タイトル：title → name
  const titleText = useMemo(() => {
    if (!quiz) return '';
    if (quiz.title) return pickLocalizedText(quiz.title);
    if (quiz.name) return quiz.name;
    return '';
  }, [quiz, pickLocalizedText]);

  // 質問文：question → summary → description
  const questionText = useMemo(() => {
    if (!quiz) return '';
    if (quiz.question) return pickLocalizedText(quiz.question);
    if (quiz.summary) return pickLocalizedText(quiz.summary);
    if (quiz.description) return quiz.description;
    return '';
  }, [quiz, pickLocalizedText]);

  // クイズが切り替わったら再生状態の表示もリセット
  useEffect(() => {
    setIsAudioPlaying(false);
  }, [quiz?.id]);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose?.();
  };

  const handleAnswerClick = (answer) => {
    if (isCompleted || !quiz?.isNearby) return;
    const ok = isCorrectAnswer(answer, correctText);
    onShowResult?.(ok);
    setSelectedAnswers([]);
  };

  const handleCellClick = (char) => {
    if (isCompleted) return;
    setSelectedAnswers((prev) =>
      prev.includes(char) ? prev.filter((c) => c !== char) : [...prev, char]
    );
  };

  // 音声再生（1回目:再生 → 2回目:停止 → 3回目:最初から再生）
  const handlePlayAudio = async () => {
    if (!quiz?.audioPath) {
      console.warn('No audio path available for this quiz');
      return;
    }

    // 再生中に押された場合は停止だけして終了
    if (isAudioPlaying) {
      audioManager.stop();
      setIsAudioPlaying(false);
      return;
    }

    // 停止中に押された場合は（1回目でも3回目でも）最初から再生
    setIsAudioPlaying(true);
    try {
      console.log('Playing audio from QuizModal:', quiz.audioPath);
      await audioManager.play(quiz.audioPath, {
        onEnded: () => setIsAudioPlaying(false)
      });
    } catch (error) {
      console.error('Failed to play audio from QuizModal:', error);
      audioManager.playSimple(quiz.audioPath, {
        onEnded: () => setIsAudioPlaying(false)
      });
    }

    // ★ 位置情報(saveLocation)と同じセッション単位で再生回数をカウント
    //    （失敗しても再生自体には影響させない）
    saveAudioPlay(sessionId, quiz.id).catch((err) => {
      console.error('Failed to record audio play count:', err);
    });
  };

  // クリア済みビュー
  const CompletedView = (
    <div style={{ textAlign: 'center', padding: '10px 8px 0' }}>
      <div style={{ fontWeight: 800, color: '#333', marginTop: 10 }}>
        {t('quiz.completed', 'クリア済み！')}
      </div>
      {correctText && (
        <div style={{ fontSize: 13, color: '#666', marginTop: 6 }}>
          {t('quiz.correctAnswer', '正解：')}
          <span style={{ fontWeight: 700 }}>{correctText}</span>
        </div>
      )}
      <div
        className="quiz-answers"
        style={{ opacity: 0.35, pointerEvents: 'none', marginTop: 12 }}
        aria-hidden
      >
        {options.length > 0
          ? options.map((choice, i) => (
              <button key={i} className="answer-btn" disabled>
                {choice}
              </button>
            ))
          : <div className="quiz-no-choices">{t('quiz.noChoices', '選択肢がありません')}</div>}
      </div>
      <div style={{ marginTop: 10, fontSize: 12, color: '#888' }}>
        {t('quiz.noRetry', 'このクイズは回答済みのため、再回答はできません。')}
      </div>
    </div>
  );

  if (!isOpen || !quiz) return null;

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="quiz-modal">
        <button className="close-btn" onClick={onClose}>✕</button>

        {/* ヘッダー */}
        <div className="quiz-header">
          <div className="quiz-badge" aria-hidden>
            <img
              src={BADGE_SRC}
              alt=""
              className="quiz-badge-img"
              onError={(e) => (e.currentTarget.style.display = 'none')}
            />
          </div>
          <h2 className="quiz-title">{titleText}</h2>
        </div>

        {/* サムネイル＋右側音声ボタン */}
        {thumbnailSrc && (
          <div className="quiz-illustration-row">
            <div className="quiz-illustration-wrap" aria-hidden>
              <img
                src={thumbnailSrc}
                alt=""
                className="quiz-illustration"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            </div>

            {quiz.audioPath && (
              <button
                className="audio-play-button quiz-audio-right"
                onClick={handlePlayAudio}
                aria-pressed={isAudioPlaying}
              >
                {isAudioPlaying ? '⏸️' : '🔊'}
              </button>
            )}
          </div>
        )}

        {/* 文字グリッド */}
        {!!quiz.grid?.length && !isCompleted && (
          <div className="quiz-grid">
            {quiz.grid.map((row, rowIndex) => (
              <div key={rowIndex} className="quiz-row">
                {row.map((char, colIndex) => (
                  <div
                    key={`${rowIndex}-${colIndex}`}
                    className={`quiz-cell ${selectedAnswers.includes(char) ? 'selected' : ''}`}
                    onClick={() => handleCellClick(char)}
                    role="button"
                  >
                    {char}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}

        <p className="quiz-question">{questionText}</p>

        {/*
        {quiz.onRouteSearch && (
          <button
            onClick={quiz.onRouteSearch}
            disabled={quiz.routeLoading}
            style={{
              marginTop: '12px',
              padding: '10px',
              width: '100%',
              borderRadius: '8px',
              border: 'none',
              background: '#ff4d4f',
              color: '#fff',
              fontWeight: 'bold',
              cursor: quiz.routeLoading ? 'not-allowed' : 'pointer'
            }}
          >
            {quiz.routeLoading ? '経路検索中...' : 'ここまでの経路'}
          </button>
        )}
        */}

        {isCompleted ? (
          CompletedView
        ) : !quiz.isNearby ? (
          <div className="quiz-locked">
            {t('quiz.moveCloser', 'このクイズに挑戦するにはスポットに近づいてください')}
          </div>
        ) : (
          <div className="quiz-answers">
            {options.length > 0 ? (
              options.map((choice, index) => (
                <button
                  key={index}
                  className="answer-btn"
                  onClick={() => handleAnswerClick(choice)}
                >
                  {choice}
                </button>
              ))
            ) : (
              <div className="quiz-no-choices">
                {t('quiz.noChoices', '選択肢がありません')}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}